import { useEffect, useMemo, useRef, useState } from 'react'
import { useAtom, useAtomValue } from 'jotai'
import { Dialog, DialogContent } from '@duck/ui'
import { ChallengeService } from '@duck/api-definitions'
import { apis } from '@modules/apiClients'
import {
  workspaceSettingsOpenAtom,
  workspaceChallengeAtom,
  workspaceTaskAtom,
} from '@/store/workspaceAtoms'
import { useCanUsePython } from '@/hooks/useCanUsePython'
import { trackWorkspaceSettings } from '@/modules/workspaceSettingsAnalytics'
import { WorkspaceSettingsNav, type SettingsNavKey } from './WorkspaceSettingsNav'
import { ChallengePanel } from './panels/ChallengePanel'
import { AppearancePanel } from './panels/AppearancePanel'
import { BehaviorPanel } from './panels/BehaviorPanel'

export const WorkspaceSettingsDialog = () => {
  const [open, setOpen] = useAtom(workspaceSettingsOpenAtom)
  const canUsePython = useCanUsePython()
  const task = useAtomValue(workspaceTaskAtom)
  const [challenge, setChallenge] = useAtom(workspaceChallengeAtom)

  const allowedWeapons = useMemo<ChallengeService.TaskWeaponsOfChoiceEnum[]>(
    () => task?.allowedWeapons ?? [ChallengeService.TaskWeaponsOfChoiceEnum.Sql],
    [task?.allowedWeapons]
  )
  const chosenWeapon =
    challenge?.workState?.chosenWeapon ??
    allowedWeapons[0] ??
    ChallengeService.TaskWeaponsOfChoiceEnum.Sql
  const showChallenge = allowedWeapons.length > 1 && canUsePython

  const [navKey, setNavKey] = useState<SettingsNavKey>(showChallenge ? 'challenge' : 'appearance')

  // Tracks dwell time and aggregate counters for the close event.
  const openedAtRef = useRef<number | null>(null)
  const changesMadeRef = useRef(0)
  const navSwitchesRef = useRef(0)

  useEffect(() => {
    if (!showChallenge && navKey === 'challenge') setNavKey('appearance')
  }, [showChallenge, navKey])

  const handleOpenChange = (next: boolean) => {
    if (next) {
      openedAtRef.current = Date.now()
      changesMadeRef.current = 0
      navSwitchesRef.current = 0
    } else if (openedAtRef.current != null) {
      trackWorkspaceSettings('workspace_settings:close', {
        challengeId: challenge?.id,
        taskId: task?.id,
        dwellMs: Date.now() - openedAtRef.current,
        lastNavKey: navKey,
        changesMade: changesMadeRef.current,
        navSwitches: navSwitchesRef.current,
      })
      openedAtRef.current = null
    }
    setOpen(next)
    if (!next) setNavKey(showChallenge ? 'challenge' : 'appearance')
  }

  const handleNavSelect = (next: SettingsNavKey) => {
    if (next === navKey) return
    navSwitchesRef.current += 1
    trackWorkspaceSettings('workspace_settings:nav', {
      challengeId: challenge?.id,
      from: navKey,
      to: next,
    })
    setNavKey(next)
  }

  const handleChosenWeaponChange = async (weapon: ChallengeService.TaskWeaponsOfChoiceEnum) => {
    if (!challenge?.id) return
    const previousWeapon = chosenWeapon
    if (previousWeapon === weapon) return
    const nextWorkState = { ...(challenge.workState ?? {}), chosenWeapon: weapon }
    setChallenge({ ...challenge, workState: nextWorkState })
    changesMadeRef.current += 1
    trackWorkspaceSettings('workspace_settings:challenge:weapon_change', {
      challengeId: challenge.id,
      taskId: task?.id,
      from: previousWeapon,
      to: weapon,
      allowedWeapons,
    })
    try {
      await apis.challengeService.saveWork({
        challengeId: challenge.id,
        workState: nextWorkState,
      })
    } catch (e) {
      console.error('Failed to persist chosenWeapon', e)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        width="none"
        noMinHeight
        className="!p-0 w-[720px] sm:w-[720px] sm:max-w-[720px] h-[480px] sm:h-[480px] sm:max-h-[480px] sm:!rounded-sm"
      >
        <div className="flex h-full w-full min-h-0 flex-row overflow-hidden">
          <WorkspaceSettingsNav active={navKey} onSelect={handleNavSelect} showChallenge={showChallenge} />

          <div className="flex-1 overflow-y-auto p-6">
            {navKey === 'challenge' && showChallenge && (
              <ChallengePanel
                allowedWeapons={allowedWeapons}
                chosenWeapon={chosenWeapon}
                onChosenWeaponChange={handleChosenWeaponChange}
              />
            )}
            {navKey === 'appearance' && <AppearancePanel onChange={() => { changesMadeRef.current += 1 }} />}
            {navKey === 'behavior' && <BehaviorPanel onChange={() => { changesMadeRef.current += 1 }} />}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
